/**
 * Copia texto al portapapeles con respaldo para navegadores que bloquean la Clipboard API
 * (permissions policy, documento sin foco, contexto no seguro). Nunca lanza: retorna
 * true solo si la copia se concretó.
 */
export async function copyTextToClipboard(text) {
  const value = text == null ? '' : String(text);
  if (!value) return false;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (err) {
      // Cae al método heredado, que no depende de la Clipboard API.
    }
  }

  return copyWithExecCommand(value);
}

/** false en http:// fuera de localhost: el navegador no expone la Clipboard API. */
export function canCopyImageToClipboard() {
  return Boolean(
    typeof window !== 'undefined' &&
      window.isSecureContext &&
      navigator.clipboard?.write &&
      typeof ClipboardItem !== 'undefined'
  );
}

/**
 * Copia un PNG al portapapeles. Recibe una promesa del blob y arma el ClipboardItem
 * en el mismo tick del clic: si se espera a generar la imagen antes de llamar a
 * write(), el navegador ya perdió la activación del usuario y rechaza la copia.
 * Retorna false si el navegador no lo permite.
 */
export async function copyPngBlobToClipboard(blobPromise) {
  if (!canCopyImageToClipboard()) return false;
  try {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': Promise.resolve(blobPromise) })]);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * PNG del SVG ya renderizado, al tamaño real del viewBox y con el texto en
 * vectores. Mermaid mete las etiquetas en foreignObject; html2canvas las
 * pixela y dibujar ese SVG en un canvas lo marca como tainted.
 */
export async function svgElementToPngBlob(svgEl, { scale = 3, background = '#f8fafc' } = {}) {
  if (!svgEl) throw new Error('No hay diagrama para copiar');
  const clone = svgEl.cloneNode(true);
  replaceForeignObjectsWithSvgText(clone);

  const box = viewBoxSize(clone);
  const pad = 24;
  const width = box.width + pad * 2;
  const height = box.height + pad * 2;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('viewBox', `${box.x - pad} ${box.y - pad} ${width} ${height}`);
  clone.removeAttribute('style');

  const xml = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(width * scale));
    canvas.height = Math.max(1, Math.ceil(height * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No se pudo generar el PNG'))), 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function viewBoxSize(svgEl) {
  const parts = String(svgEl.getAttribute('viewBox') || '')
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
    return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
  }
  const box = svgEl.getBBox();
  return { x: box.x, y: box.y, width: box.width, height: box.height };
}

function replaceForeignObjectsWithSvgText(svgEl) {
  const ns = 'http://www.w3.org/2000/svg';
  for (const fo of [...svgEl.querySelectorAll('foreignObject')]) {
    const label = (fo.textContent || '').replace(/\s+/g, ' ').trim();
    const x = parseFloat(fo.getAttribute('x') || '0');
    const y = parseFloat(fo.getAttribute('y') || '0');
    const w = parseFloat(fo.getAttribute('width') || '0');
    const h = parseFloat(fo.getAttribute('height') || '0');
    if (!label) {
      fo.remove();
      continue;
    }
    const text = document.createElementNS(ns, 'text');
    text.setAttribute('x', String(x + w / 2));
    text.setAttribute('y', String(y + h / 2));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
    text.setAttribute('font-size', String(Math.max(13, Math.min(16, Math.floor(h * 0.62) || 14))));
    text.setAttribute('fill', '#1f2937');
    text.textContent = label;
    fo.replaceWith(text);
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo generar la imagen del diagrama'));
    img.src = url;
  });
}

function copyWithExecCommand(value) {
  if (typeof document === 'undefined' || !document.body) return false;

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  // Fuera de vista pero seleccionable: display:none impediría la selección.
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.left = '-1000px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  try {
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    return document.execCommand('copy');
  } catch (err) {
    return false;
  } finally {
    document.body.removeChild(textarea);
    if (selection && previousRange) {
      selection.removeAllRanges();
      selection.addRange(previousRange);
    }
  }
}
